// src/utils/sonsKlasys.js
// Les sons de l'administration : un seul contexte audio pour toute la page.
//
// Les navigateurs refusent de jouer un son tant que l'utilisateur n'a rien
// touché dans la page. Or l'arrivée dans l'administration recharge toute la
// page après la connexion (WelcomeScreen) : le clic « Se connecter » ne compte
// pas pour ce nouveau document. Un son demandé trop tôt est donc mis en
// attente, et joué au premier clic ou à la première touche. Une fois le
// contexte déverrouillé, les sons déclenchés par une minuterie passent.
//
// Les sons sont synthétisés (Web Audio) : aucun fichier à servir.
//   - « alerte »       : le panneau des alertes critiques, insistant ;
//   - « notification » : une nouvelle notification dans la cloche, discret.

const MOTIFS = {
  // [fréquence en Hz, début en s, durée en s]
  alerte: [[880, 0, 0.18], [660, 0.22, 0.18], [880, 0.44, 0.32]],
  notification: [[660, 0, 0.12], [880, 0.14, 0.2]],
};

const VOLUMES = { alerte: 0.35, notification: 0.2 };

let contexte = null;
let enAttente = null;
let deverrouillageInstalle = false;

function obtenirContexte() {
  if (contexte) return contexte;
  if (typeof window === 'undefined') return null;
  const Contexte = window.AudioContext || window.webkitAudioContext;
  if (!Contexte) return null;
  try {
    contexte = new Contexte();
  } catch {
    contexte = null;
  }
  return contexte;
}

function jouerMaintenant(type) {
  const ctx = obtenirContexte();
  if (!ctx || ctx.state !== 'running') return false;
  const depart = ctx.currentTime + 0.02;
  for (const [frequence, debut, duree] of MOTIFS[type]) {
    const oscillateur = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillateur.type = 'sine';
    oscillateur.frequency.value = frequence;
    gain.gain.setValueAtTime(VOLUMES[type], depart + debut);
    gain.gain.exponentialRampToValueAtTime(0.001, depart + debut + duree);
    oscillateur.connect(gain);
    gain.connect(ctx.destination);
    oscillateur.start(depart + debut);
    oscillateur.stop(depart + debut + duree + 0.02);
  }
  return true;
}

function jouerLAttente() {
  if (!enAttente) return;
  const type = enAttente;
  if (jouerMaintenant(type)) enAttente = null;
}

/**
 * Déverrouille le son au premier geste de l'utilisateur, puis joue le son
 * resté en attente. À appeler une fois, au montage de la coquille.
 */
export function installerDeverrouillageAudio() {
  if (deverrouillageInstalle || typeof document === 'undefined') return;
  deverrouillageInstalle = true;

  const deverrouiller = () => {
    const ctx = obtenirContexte();
    if (!ctx) return;
    if (ctx.state === 'running') {
      jouerLAttente();
      return;
    }
    ctx.resume().then(jouerLAttente).catch(() => {});
  };

  ['pointerdown', 'keydown'].forEach((evenement) =>
    document.addEventListener(evenement, deverrouiller, { capture: true, passive: true })
  );
}

/**
 * Joue un son, ou le garde pour le premier geste si le navigateur le refuse
 * encore. Une alerte en attente n'est jamais remplacée par une notification.
 */
export function jouerSon(type = 'notification') {
  if (!MOTIFS[type]) return;
  const ctx = obtenirContexte();
  if (!ctx) return;
  if (jouerMaintenant(type)) return;
  if (enAttente !== 'alerte') enAttente = type;
  ctx.resume().then(jouerLAttente).catch(() => {});
}
